import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { SpellUsageBossOption, SpellUsageFightFilter } from '../types';
import { AggregatedHealer, TbcAnalyzerLoadResult, TbcAnalyzerPhase, TbcSessionSnapshot } from './types';
import {
    parseLogUrls,
    readStoredEncounterIds,
    readStoredFightFilter,
    readStoredString,
    TBC_ANALYZER_STORAGE,
    writeStoredEncounterIds,
    writeStoredString,
} from './utils';
import { useWclProgress } from './useWclProgress';

interface UseTbcAnalyzerSessionOptions {
    /** Legacy storage key to migrate log URLs from, if present. */
    legacyLogUrlsKey?: string;
    /** Player list for the load-logs step. Defaults to healers. */
    roleFilter?: 'healers' | 'all';
}

export function useTbcAnalyzerSession(options: UseTbcAnalyzerSessionOptions = {}) {
    const initialLogUrls = (() => {
        const shared = readStoredString(TBC_ANALYZER_STORAGE.logUrls);
        if (shared.trim().length > 0) {
            return shared;
        }
        if (options.legacyLogUrlsKey) {
            return readStoredString(options.legacyLogUrlsKey);
        }
        return '';
    })();

    const [logUrlsText, setLogUrlsText] = useState(initialLogUrls);
    const [players, setPlayers] = useState<AggregatedHealer[]>([]);
    const [bosses, setBosses] = useState<SpellUsageBossOption[]>([]);
    const [selectedPlayerName, setSelectedPlayerName] = useState(
        readStoredString(TBC_ANALYZER_STORAGE.selectedPlayerName)
    );
    const [fightFilter, setFightFilter] = useState<SpellUsageFightFilter>(readStoredFightFilter());
    const [selectedEncounterIds, setSelectedEncounterIds] = useState<number[]>(readStoredEncounterIds());
    const [minDurationSeconds, setMinDurationSeconds] = useState(
        readStoredString(TBC_ANALYZER_STORAGE.minDurationSeconds)
    );
    const [loadedLogUrls, setLoadedLogUrls] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [phase, setPhase] = useState<TbcAnalyzerPhase>('url');
    const [error, setError] = useState<string | null>(null);
    const wclProgress = useWclProgress();

    useEffect(() => {
        writeStoredString(TBC_ANALYZER_STORAGE.logUrls, logUrlsText);
    }, [logUrlsText]);

    useEffect(() => {
        writeStoredString(TBC_ANALYZER_STORAGE.fightFilter, fightFilter);
    }, [fightFilter]);

    useEffect(() => {
        writeStoredEncounterIds(selectedEncounterIds);
    }, [selectedEncounterIds]);

    useEffect(() => {
        writeStoredString(TBC_ANALYZER_STORAGE.selectedPlayerName, selectedPlayerName);
    }, [selectedPlayerName]);

    useEffect(() => {
        writeStoredString(TBC_ANALYZER_STORAGE.minDurationSeconds, minDurationSeconds);
    }, [minDurationSeconds]);

    const resetSelectionDefaults = useCallback((nextBosses: SpellUsageBossOption[] = []) => {
        setFightFilter('kills');
        setSelectedEncounterIds(nextBosses.map((boss) => boss.encounterId));
        setMinDurationSeconds('');
    }, []);

    /** Load players and bosses for these logs without changing phase; null on failure (error is set). */
    const fetchLogs = useCallback(
        async (urls: string[]): Promise<TbcAnalyzerLoadResult | null> => {
            setLoading(true);
            setError(null);
            setPlayers([]);
            setBosses([]);
            setSelectedPlayerName('');
            const wclProgressId = wclProgress.begin();

            try {
                const playersResponse = await axios.post('/api/attendance/get-players', {
                    logUrls: urls,
                    roleFilter: options.roleFilter === 'all' ? 'all' : 'healers',
                    wclProgressId,
                });

                const loadedPlayers: AggregatedHealer[] = playersResponse.data.players;
                const loadedBosses: SpellUsageBossOption[] = Array.isArray(playersResponse.data.bosses)
                    ? playersResponse.data.bosses
                    : [];
                setPlayers(loadedPlayers);
                setBosses(loadedBosses);
                setLoadedLogUrls(urls);
                resetSelectionDefaults(loadedBosses);

                if (loadedPlayers.length > 0) {
                    setSelectedPlayerName(loadedPlayers[0].name);
                }

                return { players: loadedPlayers, bosses: loadedBosses, logUrls: urls };
            } catch (err: any) {
                console.error('Error fetching data:', err);
                setError(
                    err.response?.data?.error ||
                        (options.roleFilter === 'all'
                            ? 'Failed to fetch players. Please check the URLs and try again.'
                            : 'Failed to fetch healers. Please check the URLs and try again.')
                );
                return null;
            } finally {
                wclProgress.end();
                setLoading(false);
            }
        },
        [options.roleFilter, resetSelectionDefaults, wclProgress.begin, wclProgress.end]
    );

    const handleLoadLogs = useCallback(async () => {
        const urls = parseLogUrls(logUrlsText);
        if (urls.length === 0) {
            setError('Paste at least one Warcraft Logs URL');
            return false;
        }
        const loaded = await fetchLogs(urls);
        if (!loaded) {
            return false;
        }
        setPhase('selection');
        return true;
    }, [fetchLogs, logUrlsText]);

    const handleBackToSelection = useCallback(() => {
        setPhase('selection');
        setError(null);
    }, []);

    const handleBackToUrl = useCallback(() => {
        setPhase('url');
        setPlayers([]);
        setBosses([]);
        setLoadedLogUrls([]);
        setError(null);
    }, []);

    const snapshot = useCallback(
        (): TbcSessionSnapshot => ({
            players,
            bosses,
            loadedLogUrls,
            selectedPlayerName,
            fightFilter,
            selectedEncounterIds,
            minDurationSeconds,
        }),
        [bosses, fightFilter, loadedLogUrls, minDurationSeconds, players, selectedEncounterIds, selectedPlayerName]
    );

    const restoreSnapshot = useCallback((next: TbcSessionSnapshot) => {
        setPlayers(next.players);
        setBosses(next.bosses);
        setLoadedLogUrls(next.loadedLogUrls);
        setSelectedPlayerName(next.selectedPlayerName);
        setFightFilter(next.fightFilter);
        setSelectedEncounterIds(next.selectedEncounterIds);
        setMinDurationSeconds(next.minDurationSeconds);
        setError(null);
        setPhase('results');
    }, []);

    const validateBeforeAnalyze = useCallback((): string | null => {
        if (!selectedPlayerName) {
            return 'Please select a healer for analysis';
        }
        if (loadedLogUrls.length === 0) {
            return 'No logs loaded';
        }
        if (bosses.length > 0 && selectedEncounterIds.length === 0) {
            return 'Please select at least one boss';
        }
        return null;
    }, [bosses.length, loadedLogUrls.length, selectedEncounterIds.length, selectedPlayerName]);

    const parsedMinDuration = Number(minDurationSeconds);
    const minDurationValue = Number.isFinite(parsedMinDuration) && parsedMinDuration > 0 ? parsedMinDuration : 0;

    const dedupeFightsAcrossLogs = loadedLogUrls.length > 1;
    const allBossesSelected =
        bosses.length > 0 && bosses.every((boss) => selectedEncounterIds.includes(boss.encounterId));

    const sharedRequestBody = {
        logUrls: loadedLogUrls,
        selectedPlayerName,
        fightFilter,
        encounterIds: allBossesSelected ? [] : selectedEncounterIds,
        minDurationSeconds: minDurationValue,
        dedupeFightsAcrossLogs,
    };

    const clearBossFilterMode = useCallback(() => {
        setFightFilter((current) => (current.startsWith('boss:') ? 'encounters' : current));
    }, []);

    const toggleEncounterId = useCallback(
        (encounterId: number) => {
            clearBossFilterMode();
            setSelectedEncounterIds((current) => {
                if (current.includes(encounterId)) {
                    return current.filter((id) => id !== encounterId);
                }
                return [...current, encounterId];
            });
        },
        [clearBossFilterMode]
    );

    const toggleAllEncounterIds = useCallback(() => {
        clearBossFilterMode();
        setSelectedEncounterIds((current) => {
            const allIds = bosses.map((boss) => boss.encounterId);
            const allOn = allIds.length > 0 && allIds.every((id) => current.includes(id));
            return allOn ? [] : allIds;
        });
    }, [bosses, clearBossFilterMode]);

    const toggleRaidEncounterIds = useCallback(
        (raidEncounterIds: number[]) => {
            clearBossFilterMode();
            setSelectedEncounterIds((current) => {
                const allOn = raidEncounterIds.length > 0 && raidEncounterIds.every((id) => current.includes(id));
                if (allOn) {
                    const remove = new Set(raidEncounterIds);
                    return current.filter((id) => !remove.has(id));
                }
                const next = new Set(current);
                for (const id of raidEncounterIds) {
                    next.add(id);
                }
                return bosses.map((boss) => boss.encounterId).filter((id) => next.has(id));
            });
        },
        [bosses, clearBossFilterMode]
    );

    return {
        logUrlsText,
        setLogUrlsText,
        players,
        bosses,
        selectedPlayerName,
        setSelectedPlayerName,
        fightFilter,
        setFightFilter,
        selectedEncounterIds,
        setSelectedEncounterIds,
        toggleEncounterId,
        toggleAllEncounterIds,
        toggleRaidEncounterIds,
        minDurationSeconds,
        setMinDurationSeconds,
        minDurationValue,
        loadedLogUrls,
        loading,
        setLoading,
        phase,
        setPhase,
        error,
        setError,
        fetchLogs,
        handleLoadLogs,
        handleBackToSelection,
        handleBackToUrl,
        validateBeforeAnalyze,
        snapshot,
        restoreSnapshot,
        sharedRequestBody,
        dedupeFightsAcrossLogs,
        beginWclProgress: wclProgress.begin,
        endWclProgress: wclProgress.end,
        wclSnapshot: wclProgress.snapshot,
    };
}
